import { EventEmitter, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { LoggerService } from "./logger.service";
import { AuthService } from "../../core/authentication/auth.service";
import { TelemetryService } from "../../shared/services/telemetry.service";
import { environment } from "../../core/config/environment";
import { db } from "../../core/database/db";
import { UuidService } from "./uuid.service";
import JSZip from "jszip";
import { getEnvironmentName } from "../../shared/utils/environment-helper";
import { BqaJobSyncMetadata, DataSyncMetadataService } from "../../core/sync/data-sync/data-sync-metadata.service";
import { HashedUserIdService } from "../../core/authentication/hashed-userId.service";
import { firstValueFrom } from "rxjs";

const BLOCK_SIZE = 512 * 1024;   // 512 KiB
const CONCURRENCY = 3;            // parallel HTTP PUTs

export interface DiagnosticMetadata {
  userId: string;
  sessionId: string;
  timestamp: string;
  appVersion: string;
  deviceInfo: {
    userAgent: string;
    screenWidth: number;
    screenHeight: number;
    language: string;
    timezone: string;
  };
  environment: string;
}

export interface DiagnosticPayload {
  diagnosticMetadata: DiagnosticMetadata;
  syncMetadata: Record<string, BqaJobSyncMetadata> | null;
  logs: string[];
  indexedDB: Record<string, unknown[]>;
}

export interface DiagnosticUploadRequest {
  fileName: string;
  fileSize: number;
}

export interface DiagnosticUploadResponse {
  uploadSasUri: string;
}

@Injectable({
  providedIn: "root"
})
export class DiagnosticService {
  private readonly sessionId: string;

  constructor(
    private readonly logger: LoggerService,
    private readonly authService: AuthService,
    private readonly telemetryService: TelemetryService,
    private readonly uuidService: UuidService,
    private readonly http: HttpClient,
    private readonly dataSyncMetadataService: DataSyncMetadataService,
    private readonly hashedUserIdService: HashedUserIdService
  ) {
    this.sessionId = this.getOrCreateSessionId();
  }

  onUploadProgress = new EventEmitter<number>();

  private getOrCreateSessionId(): string {
    const storageKey = "bqe-diagnostic-session-id";
    let sessionId = localStorage.getItem(storageKey);

    if (!sessionId) {
      sessionId = this.uuidService.v4();
      localStorage.setItem(storageKey, sessionId);
    }

    return sessionId;
  }

  private getSessionId(): string {
    const appInsightsSessionId =
      this.telemetryService.client.context.sessionManager?.automaticSession?.id;

    return appInsightsSessionId || this.sessionId;
  }


  private collectMetadata(): DiagnosticMetadata {
    const account = this.authService.currentAccount;

    return {
      userId: account?.homeAccountId || account?.username || account?.localAccountId || "unknown",
      sessionId: this.getSessionId(),
      timestamp: new Date().toISOString(),
      appVersion: environment.version,
      deviceInfo: {
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      environment: getEnvironmentName()
    };
  }

  private async collectIndexedDBData(): Promise<Record<string, unknown[]>> {
    const data: Record<string, unknown[]> = {};

    try {
      const tableNames = [
        "lot", "inspectionJob", "zone", "area", "component", "assertion",
        "assertionIssue", "userVendorContact", "jobIssue", "jobIssueVendor",
        "jobSignature", "syncStatus", "media"
      ] as const;

      // Independent table reads, safe to run concurrently
      const results = await Promise.all(tableNames.map(name => db[name].toArray()));
      tableNames.forEach((name, i) => {
        data[name] = results[i]; 
      });
    } catch (error) {
      this.logger.error("Error collecting IndexedDB data:", error);
      throw error;
    }

    return data;
  }

  /**
   * Generates the complete diagnostic payload
   */
  async generateDiagnosticPayload(): Promise<DiagnosticPayload> {
    try {
      const diagnosticMetadata = this.collectMetadata();
      const syncMetadata = this.dataSyncMetadataService.getSyncMetadata() || {};
      const logs = this.logger.getLogs();
      const indexedDB = await this.collectIndexedDBData();

      return {
        diagnosticMetadata,
        syncMetadata,
        logs,
        indexedDB
      };
    } catch (error) {
      this.logger.error("Failed to generate diagnostic payload:", error);
      throw error;
    }
  }

  async createDiagnosticZip(): Promise<Blob> {
    try {
      const payload = await this.generateDiagnosticPayload();
      const zip = new JSZip();

      zip.file("metadata.json", JSON.stringify(payload.diagnosticMetadata, null, 2));
      zip.file("logs.txt", payload.logs.join("\n"));

      const indexedDbFolder = zip.folder("indexeddb");
      if (indexedDbFolder) {
        for (const [tableName, tableData] of Object.entries(payload.indexedDB)) {
          indexedDbFolder.file(`${tableName}.json`, JSON.stringify(tableData, null, 2));
        }
      }

      // Generate zip file as Blob
      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6
        }
      });

      this.logger.info(`Diagnostic zip created: ${(zipBlob.size / 1024 / 1024).toFixed(2)} MB`);
      return zipBlob;
    } catch (error) {
      this.logger.error("Failed to create diagnostic zip:", error);
      throw error;
    }
  }

  async uploadDiagnosticBundle(): Promise<void> {
    try {
      this.logger.info("Creating diagnostic zip file...");
      const zipBlob = await this.createDiagnosticZip();

      const timestamp = new Date().toISOString()
        .replaceAll(":", "-")
        .replaceAll(".", "-");
      const sessionId = this.getSessionId();
      const hashedUserId = this.hashedUserIdService.getHashedUserId();

      const fileNameBase = `${hashedUserId}-diagnostic-bundle-${timestamp}`;
      const fileName = sessionId
        ? `${fileNameBase}-session-${sessionId}.zip`
        : `${fileNameBase}.zip`;

      const diagnosticUploadRequest: DiagnosticUploadRequest = {
        fileName,
        fileSize: zipBlob.size
      };

      const sasUri = await this.getUploadUri(diagnosticUploadRequest);

      const totalSize = zipBlob.size;
      const numberOfUploadBlocks = Math.ceil(totalSize / BLOCK_SIZE);
      let next = 0, done = 0;
      const blockIds = Array.from({ length: numberOfUploadBlocks }, (_, i) => this.makeBlockId(i));

      const uploadPart = async () => {
        while (next < numberOfUploadBlocks) {
          const idx = next++;
          const start = idx * BLOCK_SIZE;
          const end = Math.min(start + BLOCK_SIZE, totalSize);
          const chunk = zipBlob.slice(start, end);
          await this.putBlock(sasUri, blockIds[idx], chunk);
          done++;

          const percentComplete = Math.round(100 * done / numberOfUploadBlocks);
          this.onUploadProgress.emit(percentComplete);
          this.logger.info(`DiagnosticService:uploadDiagnosticBundle -- Uploaded block ${idx + 1} of ${numberOfUploadBlocks} for: ${fileName} (${percentComplete}%)`);
        }
      };

      await Promise.all(Array.from({ length: CONCURRENCY }, uploadPart));
      await this.putBlockList(sasUri, blockIds);        // blob assembled in Azure

      this.logger.info("Diagnostic bundle submitted successfully");
    } catch (error) {
      this.logger.error("Failed to upload diagnostic bundle:", error);
      throw error;
    }
  }


  private async getUploadUri(diagnosticUploadRequest: DiagnosticUploadRequest): Promise<string> {
    const url = `${environment.backend.apiBaseURL}/api/Diagnostics/GetDiagnosticUploadTicket`;
    const response = await firstValueFrom(this.http.post<DiagnosticUploadResponse>(url, diagnosticUploadRequest));
    return response.uploadSasUri;
  }

  private makeBlockId(n: number) {
    return btoa(String(n).padStart(8, "0")); // 00000000 ⇒ base64
  }

  private async putBlock(uri: string, id: string, body: Blob) {
    const response = await fetch(
      `${uri}&comp=block&blockid=${encodeURIComponent(id)}`,
      {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob" },
        body
      }
    );

    if (!response.ok) {
      throw new Error(`putBlock failed: ${response.status} ${response.statusText}`);
    }

    return response;
  };

  private async putBlockList(uri: string, ids: string[]) {
    const response = await fetch(`${uri}&comp=blocklist`, {
      method: "PUT",
      headers: { "Content-Type": "application/xml" },
      body: `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map(i => `<Latest>${i}</Latest>`).join("")}</BlockList>`
    });

    if (!response.ok) {
      throw new Error(`putBlockList failed: ${response.status} ${response.statusText}`);
    }

    return response;
  };
}

